import { useEffect, useMemo, useState } from "react";
import type { SnowflakeId } from "@/types/snowflake";
import { useCatalog } from "./useLookup";
import { positionsApi } from "@/features/catalogs/catalogCrudApi";

/**
 * 解析某 position code 的 work_type_codes（供用户详情 flyout 的「继承自 position」展示）。
 *
 * position catalog 的 /lookup 只返回 id/code/label/serviceLineCode（不带 work_type_codes），
 * 所以这里先用已加载的 lookup 把 code → id，再 GET /system/positions/{id} 拿完整记录里的
 * workTypeCodes。position code 变化时重新解析。
 */
export function usePositionWorkTypes(positionTypeCode?: string | null) {
  const { data: positions } = useCatalog("position");

  const position = useMemo(
    () => (positions ?? []).find((p: any) => p.code === positionTypeCode),
    [positions, positionTypeCode],
  );
  const positionId = position?.id as SnowflakeId | undefined;
  const positionLabel = position?.label as string | undefined;

  const [positionWorkTypes, setPositionWorkTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!positionId) {
      setPositionWorkTypes([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    positionsApi
      .get(positionId)
      .then((rec: any) => {
        if (cancelled) return;
        // 契约:GET /system/positions/{id} 应含 workTypeCodes[]。缺失=后端契约缺口,dump 出来便于排查。
        if (rec && !Array.isArray(rec.workTypeCodes)) {
          console.warn(
            "[usePositionWorkTypes] GET /system/positions/%s 缺少 workTypeCodes[],response shape:",
            positionId,
            rec,
          );
        }
        setPositionWorkTypes(Array.isArray(rec?.workTypeCodes) ? rec.workTypeCodes : []);
      })
      .catch((e) => {
        // 失败大声记录(后端契约 GET /system/positions/{id} 应含 workTypeCodes),UI 退化为空继承集
        console.error("[usePositionWorkTypes] failed to load position work types:", e);
        if (!cancelled) setPositionWorkTypes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [positionId]);

  return { positionWorkTypes, positionLabel, loading };
}
